/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo } from "react";
import { ITreeNode } from "../../ui/components/Tree/TreeViewTypes";
import {
	ISchemaComponentListModel,
	isModel,
	isSchemaComponentListModel,
	isSchemaComponentModel,
	isSchemaConstArrayModel,
	isSchemaConstMapModel,
	isSchemaConstMapModelEntry,
	isSchemaDataSourceListModel,
	isSchemaDataSourceModel,
	QUERY_FN_OP,
	queryModelParents,
	TGenericModelNode
} from "@adapptio/lib-blueprint";
import { Icon, ListItemIcon, ListItemText, MenuItem, MenuList } from "@adapptio/lib-editor-ui";
import { useCommand } from "../../ui/hooks";
import { ISchemaDataSourceListModel } from "@adapptio/lib-blueprint/src/schemas/SchemaDataSourceList";

export interface IContextMenuOutlineNodeProps {
	treeNode: ITreeNode;
}

export const ContextMenuOutlineNode: React.FC<IContextMenuOutlineNodeProps> = ({ treeNode }) => {
	const mapModel = treeNode.metaData?.mapModel;
	const mapEntry = treeNode.metaData?.mapEntry;
	const modelNode = treeNode.metaData?.modelNode as TGenericModelNode;
	const primaryListModel = treeNode.metaData?.primaryListModel;

	const [ duplicateComponent ] = useCommand("duplicate.component");
	const [ duplicateDataSource ] = useCommand("duplicate.dataSource");
	const [ duplicateMapModel ] = useCommand("duplicate.mapModel");
	const [ duplicateArrayItem ] = useCommand("duplicate.arrayItem");

	if (
		!isSchemaConstMapModel(mapModel) &&
		!isSchemaConstMapModelEntry(mapEntry) &&
		!isSchemaDataSourceModel(modelNode) &&
		!isSchemaComponentModel(modelNode) &&
		!isSchemaDataSourceModel(primaryListModel) &&
		!isSchemaConstArrayModel(primaryListModel)
	) {
		return null;
	}

	const handleDelete = useCallback(() => {
		if (isSchemaDataSourceModel(modelNode)) {
			modelNode.schema.removeSelfFromDataSourceList(modelNode, true, true);
			return;
		}

		if (isSchemaDataSourceModel(primaryListModel)) {
			primaryListModel.schema.removeSelfFromDataSourceList(primaryListModel, true, true);
			return;
		}

		if (isSchemaComponentModel(modelNode)) {
			modelNode.schema.removeSelfFromComponentList(modelNode, true, true);
			return;
		}

		if (isSchemaConstMapModel(mapModel) && isSchemaConstMapModelEntry(mapEntry)) {
			const index = mapModel.items.findIndex((item) => item.key.nodeId === mapEntry.key.nodeId);

			if (index > -1) {
				mapModel.schema.removeElement(mapModel, index, true);
			}

			return;
		}

		if (isSchemaConstArrayModel(primaryListModel) && isModel(modelNode)) {
			const index = primaryListModel.items.findIndex((item) => item.nodeId === modelNode.nodeId);

			if (index > -1) {
				primaryListModel.schema.removeElement(primaryListModel, index, true);
			}

			return;
		}
	}, [ mapModel, mapEntry, modelNode, primaryListModel ]);

	const handleDuplicate = useCallback(() => {
		if (isSchemaComponentModel(modelNode)) {
			duplicateComponent(modelNode);
		}
		if (isSchemaDataSourceModel(modelNode)) {
			duplicateDataSource(modelNode);
		}
		if (isSchemaConstMapModel(mapModel) && isSchemaConstMapModelEntry(mapEntry)) {
			duplicateMapModel(mapModel, mapEntry);
		}
		if (isSchemaConstArrayModel(primaryListModel) && isModel(modelNode)) {
			duplicateArrayItem(modelNode, primaryListModel);
		}
	}, [ mapModel, mapEntry, modelNode, primaryListModel ]);

	const handleMoveUp = useCallback(() => {
		if (isSchemaComponentModel(modelNode)) {
			modelNode.parentComponentList.schema.moveItemByModelUp(modelNode.parentComponentList, modelNode, true);
			return;
		}

		if (isSchemaDataSourceModel(modelNode)) {
			modelNode.parentDataSourceList.schema.moveItemByModelUp(modelNode.parentDataSourceList, modelNode, true);
			return;
		}

		if (isSchemaConstMapModel(mapModel) && isSchemaConstMapModelEntry(mapEntry)) {
			mapModel.schema.moveElementByModelUp(mapModel, mapEntry, true);
			return;
		}

		if (isSchemaConstArrayModel(primaryListModel) && isModel(modelNode)) {
			primaryListModel.schema.moveElementByModelUp(primaryListModel, modelNode, true);
			return;
		}
	}, [ modelNode ]);

	const handleMoveDown = useCallback(() => {
		if (isSchemaComponentModel(modelNode)) {
			modelNode.parentComponentList.schema.moveItemByModelDown(modelNode.parentComponentList, modelNode, true);
			return;
		}

		if (isSchemaDataSourceModel(modelNode)) {
			modelNode.parentDataSourceList.schema.moveItemByModelDown(modelNode.parentDataSourceList, modelNode, true);
			return;
		}

		if (isSchemaConstMapModel(mapModel) && isSchemaConstMapModelEntry(mapEntry)) {
			mapModel.schema.moveElementByModelDown(mapModel, mapEntry, true);
			return;
		}

		if (isSchemaConstArrayModel(primaryListModel) && isModel(modelNode)) {
			primaryListModel.schema.moveElementByModelDown(primaryListModel, modelNode, true);
			return;
		}
	}, [ modelNode ]);

	const handleMoveToParent = useCallback(() => {
		if (isSchemaComponentModel(modelNode)) {
			const parentList = queryModelParents(modelNode.parentComponentList, (item) =>
				isSchemaComponentListModel(item)
					? { match: true, op: QUERY_FN_OP.BREAK }
					: { match: false, op: QUERY_FN_OP.CONTINUE }
			).pop() as ISchemaComponentListModel|null;

			if (parentList) {
				modelNode.schema.removeSelfFromComponentList(modelNode, true, false);
				parentList.schema.addItemModel(parentList, parentList.items.length, modelNode, true);
			}

			return;
		}

		if (isSchemaDataSourceModel(modelNode)) {
			const parentList = queryModelParents(modelNode.parentDataSourceList, (item) =>
				isSchemaDataSourceListModel(item)
					? { match: true, op: QUERY_FN_OP.BREAK }
					: { match: false, op: QUERY_FN_OP.CONTINUE }
			).pop() as ISchemaDataSourceListModel|null;

			if (parentList) {
				modelNode.schema.removeSelfFromDataSourceList(modelNode, true, false);
				parentList.schema.addItemModel(parentList, parentList.items.length, modelNode, true);
			}

			return;
		}
	}, []);

	const options = useMemo(() => {
		let isRootNode = true;
		let indexInParentlist: number|null = null;
		let parentListLength: number|null = null;

		if (isSchemaComponentModel(modelNode)) {
			const parentListModel = modelNode.parentComponentList;
			const topMostListModel = queryModelParents(parentListModel, (item) => ({
				match: isSchemaComponentListModel(item),
				op: QUERY_FN_OP.CONTINUE
			})).pop();

			isRootNode = topMostListModel === undefined;
			indexInParentlist = parentListModel.schema.getItemModelIndex(parentListModel, modelNode);
			parentListLength = parentListModel.items.length;
		} else if (isSchemaDataSourceModel(modelNode)) {
			const parentListModel = modelNode.parentDataSourceList;
			const topMostListModel = queryModelParents(parentListModel, (item) => ({
				match: isSchemaDataSourceListModel(item),
				op: QUERY_FN_OP.CONTINUE
			})).pop();
			
			isRootNode = topMostListModel === undefined;
			indexInParentlist = parentListModel.schema.getItemModelIndex(parentListModel, modelNode);
			parentListLength = parentListModel.items.length;
		} else if (isSchemaConstMapModelEntry(mapEntry) && isSchemaConstMapModel(mapModel)) {
			isRootNode = true;
			indexInParentlist = mapModel.items.findIndex((item) => item.key.nodeId === mapEntry.key.nodeId);
			parentListLength = mapModel.items.length;
		} else if (isModel(modelNode) && isSchemaConstArrayModel(primaryListModel)) {
			isRootNode = true;
			indexInParentlist = primaryListModel.items.findIndex((item) => item.nodeId === modelNode.nodeId);
			parentListLength = primaryListModel.items.length;
		}

		const canMoveToParent = !isRootNode;
		const canMoveUp = indexInParentlist !== null && indexInParentlist > 0;
		const canMoveDown = indexInParentlist !== null && parentListLength !== null && indexInParentlist < (parentListLength - 1);

		return { canMoveUp, canMoveDown, canMoveToParent };
	}, [modelNode, primaryListModel]);

	return (
		<MenuList>
			<MenuItem onClick={handleDelete}>
				<ListItemIcon>
					<Icon name="mdi/delete" />
				</ListItemIcon>
				<ListItemText align="left">Delete</ListItemText>
			</MenuItem>
			<MenuItem onClick={handleDuplicate}>
				<ListItemIcon>
					<Icon name="mdi/content-duplicate" />
				</ListItemIcon>
				<ListItemText align="left">Duplicate</ListItemText>
			</MenuItem>
			{options.canMoveToParent && (
				<MenuItem onClick={handleMoveToParent}>
					<ListItemIcon>
						<Icon name="mdi/arrow-up-left" />
					</ListItemIcon>
					<ListItemText align="left">Move to Parent</ListItemText>
				</MenuItem>
			)}
			{options.canMoveUp && (
				<MenuItem onClick={handleMoveUp}>
					<ListItemIcon>
						<Icon name="mdi/arrow-up" />
					</ListItemIcon>
					<ListItemText align="left">Move Up</ListItemText>
				</MenuItem>
			)}
			{options.canMoveDown && (
				<MenuItem onClick={handleMoveDown}>
					<ListItemIcon>
						<Icon name="mdi/arrow-down" />
					</ListItemIcon>
					<ListItemText align="left">Move Down</ListItemText>
				</MenuItem>
			)}
		</MenuList>
	);
};
